import { onScopeDispose, ref, type Ref } from 'vue'

import { i18n } from '@/i18n'
import type { Subscription } from '@/lib/firebase/subscribe'
import { createRetainer } from '@/lib/live/retainer'
import type { SetupScope } from '@/repositories/setup-docs'
import {
  type ScopedSubscription,
  currentSetupScope,
  useScopedSubscription,
} from '@/stores/setup-subscriptions'

/**
 * A scoped subscription that follows one key, such as a page or topic id.
 * The current key is the most recently retained key that is still held. One retainer owns the
 * listener's lifetime across all keys: after the last release the listener stays open on the
 * current key for the retainer's linger, and `releaseAllRetained`/`resumeRetained` close and
 * reopen it for the current key.
 */
export function useKeyedSubscription<T>(
  subscribe: (scope: SetupScope, key: string) => Subscription<T>,
): {
  state: ScopedSubscription<T>['state']
  key: Ref<string | null>
  retain: (key: string) => () => void
} {
  const key = ref<string | null>(null)
  const held = new Map<string, number>()
  let openFor: string | null = null
  const subscription = useScopedSubscription((scope) => subscribe(scope, openFor ?? ''))

  /** Opens the listener for the current key while the retainer is open, and closes it otherwise. */
  function sync(): void {
    const target = lifetime.isOpen ? key.value : null
    if (target === openFor) return
    subscription.close()
    openFor = target
    if (target === null) return
    const scope = currentSetupScope()
    if (scope) subscription.open(scope)
    else subscription.fail(i18n.global.t('common.noWorkspaceSelected'))
  }

  const lifetime = createRetainer({
    open: sync,
    close() {
      if (held.size === 0) key.value = null
      sync()
    },
  })

  function retain(target: string): () => void {
    const count = held.get(target) ?? 0
    held.delete(target)
    held.set(target, count + 1)
    key.value = target
    const releaseLifetime = lifetime.retain()
    sync()
    let released = false
    return () => {
      if (released) return
      released = true
      const left = (held.get(target) ?? 1) - 1
      if (left > 0) held.set(target, left)
      else {
        held.delete(target)
        if (key.value === target && held.size > 0) key.value = [...held.keys()].at(-1) ?? null
      }
      sync()
      releaseLifetime()
    }
  }

  onScopeDispose(() => lifetime.dispose())

  return { state: subscription.state, key, retain }
}
